import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  fetchVariantPricing,
  type VariantPricingRow,
} from '@/services/activityPricing'
import { ENDURO_TOUR_PRICES_USD } from '@/features/booking/enduroPricing'
import {
  ENDURO_ACTIVITY_ID,
  enduroTourVariant,
} from '@/features/booking/tourMapping'

/**
 * Compta-sourced enduro variant prices (half_day/full_day/2_day/6_day).
 * Mirrors useTrekPricing: 5-minute stale time, fallback to the hardcoded
 * ENDURO_TOUR_PRICES_USD mirror until compta responds (or if it's down).
 * Returns a Record<variant, priceUsd> ready to index.
 */
export const useEnduroPricing = (): Record<string, number> => {
  const { data } = useQuery<VariantPricingRow[]>(
    ['compta', 'variant-pricing'],
    fetchVariantPricing,
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  )
  return useMemo(() => {
    const merged: Record<string, number> = { ...ENDURO_TOUR_PRICES_USD }
    for (const r of data ?? []) {
      if (
        r.source_id === ENDURO_ACTIVITY_ID &&
        r.variant &&
        r.base_price_usd > 0
      ) {
        merged[r.variant] = r.base_price_usd
      }
    }
    return merged
  }, [data])
}

/**
 * Display price for a tour row: compta variant price when the tour maps to an
 * enduro variant, otherwise the website catalog price.
 */
export function enduroTourPrice(
  tour: { slug: string; title: string; price_from_usd: number },
  prices: Record<string, number>
): number {
  const variant = enduroTourVariant(tour)
  return (variant != null ? prices[variant] : undefined) ?? tour.price_from_usd
}
